import React, { useState, useEffect } from 'react';
import { Box, Button } from '@mui/material';
import RedditIcon from '@mui/icons-material/Reddit';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/** Resolve thumbnail URL — local Django-served paths get the API base URL prepended. */
const resolveThumbUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${API_URL}${url.replace(/\\/g, '/')}`;
};

/**
 * Fetch the highest-quality preview image for a Reddit post.
 * Uses the public JSON API — no auth required.
 */
const fetchRedditThumbnail = async (redditUrl) => {
  try {
    const match = redditUrl.match(/comments\/([a-z0-9]+)/i);
    if (!match) return null;
    // No custom headers → no CORS preflight. raw_json=1 avoids HTML-encoded URLs.
    const res = await fetch(`https://www.reddit.com/comments/${match[1]}.json?raw_json=1`);
    if (!res.ok) return null;
    const data = await res.json();
    const post = data?.[0]?.data?.children?.[0]?.data;
    // Full-size preview image
    const preview = post?.preview?.images?.[0]?.source?.url;
    if (preview) return preview;
    // Smaller thumbnail fallback (skip Reddit's non-image placeholder values)
    if (post?.thumbnail && post.thumbnail.startsWith('http')) return post.thumbnail;
    return null;
  } catch {
    return null;
  }
};

/** Returns true if the URL is a directly-playable video file.
 * v.redd.it is excluded — its .mp4 has no audio; use the embed iframe instead.
 */
const isDirectVideo = (url) => {
  if (!url) return false;
  if (url.includes('v.redd.it')) return false;
  return /\.(mp4|webm|m3u8|mov|ogg)(\?|$)/i.test(url);
};

/** Returns a clean hostname label, e.g. "Streamin.me" */
const getDomainLabel = (url) => {
  try {
    const host = new URL(url).hostname.replace(/^www\./, '');
    return host.charAt(0).toUpperCase() + host.slice(1);
  } catch {
    return 'External site';
  }
};

/**
 * Derive a redditmedia embed URL from a standard Reddit post URL.
 * e.g. https://www.reddit.com/r/soccer/comments/abc123/some_title/
 *   -> https://www.redditmedia.com/r/soccer/comments/abc123/some_title/?embed=true&ref_source=embed
 */
const getRedditEmbedUrl = (url) => {
  if (!url) return null;
  try {
    const u = new URL(url);
    // Must be a reddit.com comments link
    if (!u.hostname.includes('reddit.com') || !u.pathname.includes('/comments/')) return null;
    const embedUrl = new URL(u.pathname, 'https://www.redditmedia.com');
    embedUrl.searchParams.set('embed', 'true');
    embedUrl.searchParams.set('ref_source', 'embed');
    embedUrl.searchParams.set('theme', 'light');
    return embedUrl.toString();
  } catch {
    return null;
  }
};

// Simplified video player with thumbnail + Reddit embed support
const BasicVideoPlayer = ({ redditUrl, s3VideoUrl, thumbnailUrl, videoUrl, autoPlay, controls, isMobile }) => {
  // resolvedThumb: starts as the prop value (resolved to full URL), may be upgraded on error
  const [resolvedThumb, setResolvedThumb] = useState(resolveThumbUrl(thumbnailUrl));
  const [thumbBroken, setThumbBroken] = useState(false);

  // Reset when props change
  useEffect(() => {
    setResolvedThumb(resolveThumbUrl(thumbnailUrl));
    setThumbBroken(false);
  }, [thumbnailUrl, redditUrl]);

  // Only fetch from Reddit when the stored thumbnail is broken — never eagerly on mount.
  // This prevents 27+ simultaneous Reddit API requests when the nominations list renders.
  useEffect(() => {
    if (!thumbBroken || !redditUrl || s3VideoUrl) return;
    let cancelled = false;
    fetchRedditThumbnail(redditUrl).then((url) => {
      if (!cancelled && url) {
        setResolvedThumb(url);
        setThumbBroken(false);
      }
    });
    return () => { cancelled = true; };
  }, [thumbBroken, redditUrl, s3VideoUrl]);

  const openReddit = (e) => {
    e.stopPropagation();
    if (redditUrl) window.open(redditUrl, '_blank');
  };

  // No S3 URL — work out what to show
  if (!s3VideoUrl) {
    const embedUrl = getRedditEmbedUrl(redditUrl);

    if (autoPlay) {
      // --- MODAL MODE ---

      // 1. Direct video file (mp4, webm, m3u8…) — play natively
      if (isDirectVideo(videoUrl)) {
        return (
          <Box sx={{ width: '100%', height: '100%', bgcolor: '#000' }}>
            <video
              src={videoUrl}
              autoPlay
              controls
              playsInline
              controlsList="nodownload"
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          </Box>
        );
      }

      // 2. External (non-Reddit) video link — show thumbnail backdrop + watch button
      const isExternal = videoUrl && !videoUrl.includes('reddit.com') && !videoUrl.includes('redd.it');
      if (isExternal) {
        return (
          <Box sx={{ position: 'relative', width: '100%', height: '100%', bgcolor: '#0A0A0A', overflow: 'hidden', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2.5 }}>
            {/* Blurred thumbnail backdrop */}
            {resolvedThumb && !thumbBroken && (
              <img
                src={resolvedThumb}
                alt=""
                onError={() => setThumbBroken(true)}
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: 'blur(12px) brightness(0.35)', transform: 'scale(1.08)' }}
              />
            )}
            <Box sx={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <Box component="span" sx={{ fontSize: '0.65rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.45)', fontWeight: 600 }}>
                Hosted on {getDomainLabel(videoUrl)}
              </Box>
              <Button
                variant="contained"
                onClick={() => window.open(videoUrl, '_blank')}
                sx={{ bgcolor: 'primary.main', color: '#fff', fontWeight: 700, letterSpacing: '0.04em', px: 4, py: 1.25, '&:hover': { bgcolor: 'primary.dark' } }}
              >
                Watch on {getDomainLabel(videoUrl)}
              </Button>
              {redditUrl && (
                <Button
                  variant="text"
                  startIcon={<RedditIcon sx={{ fontSize: 16 }} />}
                  onClick={() => window.open(redditUrl, '_blank')}
                  sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.75rem', '&:hover': { color: 'rgba(255,255,255,0.75)' } }}
                >
                  View Reddit post
                </Button>
              )}
            </Box>
          </Box>
        );
      }

      // 3. Native Reddit video / fallback — use Reddit embed iframe
      if (embedUrl) {
        return (
          <Box sx={{ width: '100%', height: '100%', position: 'relative', bgcolor: '#FAF9F7' }}>
            <iframe
              src={embedUrl}
              title="Reddit video embed"
              style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
              scrolling="no"
              allowFullScreen
              sandbox="allow-scripts allow-same-origin allow-presentation allow-popups"
            />
          </Box>
        );
      }

      // 4. Nothing playable — open Reddit post
      return (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: '#0A0A0A' }}>
          {redditUrl && (
            <Button variant="outlined" color="primary" startIcon={<RedditIcon />} onClick={() => window.open(redditUrl, '_blank')}>
              View on Reddit
            </Button>
          )}
        </Box>
      );
    }

    if (embedUrl && !autoPlay) {
      // On the card (thumbnail mode), show thumbnail or a Reddit-branded placeholder
      const showThumb = resolvedThumb && !thumbBroken;
      return (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            position: 'relative',
            bgcolor: '#1A1A1A',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
          }}
        >
          {showThumb ? (
            <img
              src={resolvedThumb}
              alt=""
              onError={() => setThumbBroken(true)}
              style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', top: 0, left: 0, display: 'block' }}
            />
          ) : (
            /* Gradient placeholder while fetching or when unavailable */
            <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)' }} />
          )}

          {/* Reddit badge */}
          <Box
            sx={{
              position: 'absolute',
              top: 6,
              right: 8,
              bgcolor: 'rgba(255,255,255,0.85)',
              borderRadius: '4px',
              px: 0.75,
              py: 0.3,
              display: 'flex',
              alignItems: 'center',
              gap: 0.4,
              zIndex: 3,
            }}
          >
            <RedditIcon sx={{ fontSize: 13, color: '#FF4500' }} />
            <Box component="span" sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#111', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
              Reddit
            </Box>
          </Box>

          {/* Play button overlay */}
          <Box
            sx={{
              position: 'absolute',
              top: 0, left: 0, width: '100%', height: '100%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: showThumb ? 'rgba(0,0,0,0.28)' : 'transparent',
              zIndex: 2,
            }}
          >
            <Box sx={{ width: 46, height: 46, borderRadius: '50%', bgcolor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M8 5v14l11-7z" /></svg>
            </Box>
          </Box>
        </Box>
      );
    }

    // No embed URL — fallback button
    return (
      <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: '#F5F3EF' }}>
        {redditUrl && (
          <Button variant="outlined" color="primary" startIcon={<RedditIcon />} onClick={openReddit} size="small">
            View on Reddit
          </Button>
        )}
      </Box>
    );
  }

  if (!autoPlay) {
    return (
      <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
        {resolvedThumb && !thumbBroken ? (
          <img
            src={resolvedThumb}
            alt=""
            onError={() => setThumbBroken(true)}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              backgroundColor: '#000'
            }}
          />
        ) : (
          // Fallback to video with poster attribute
          <video
            src={s3VideoUrl}
            poster={`${s3VideoUrl}#t=0.1`}
            muted
            playsInline
            preload="metadata"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              backgroundColor: '#000'
            }}
          />
        )}
        
        {/* Play button overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(0,0,0,0.3)',
            zIndex: 2
          }}
        >
          <Box
            sx={{
              width: 50,
              height: 50,
              borderRadius: '50%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: 'rgba(0,0,0,0.6)',
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
              <path d="M8 5v14l11-7z" />
            </svg>
          </Box>
        </Box>
      </Box>
    );
  }

  // For playback mode (autoplay)
  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
      <video
        src={s3VideoUrl}
        controls={controls}
        autoPlay={autoPlay}
        muted={!autoPlay}
        playsInline
        controlsList="nodownload"
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          backgroundColor: '#000'
        }}
      />
      {isMobile && controls && (
        <Box 
          sx={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            width: '100%', 
            height: '40px', 
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, transparent 100%)',
            pointerEvents: 'none'
          }} 
        />
      )}
    </Box>
  );
};

export default BasicVideoPlayer; 